// src/components/pages/shared/BreakDetailPopover.tsx
//
// Al pinchar un bloque de descanso en el calendario, este popover muestra
// el horario y el motivo, con un botón para borrarlo. Portal posicionado por
// coordenadas del clic (no un elemento del DOM): los eventos de FullCalendar
// se remontan en cada render y no dan un nodo estable al que anclar un
// Popover normal.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Coffee, Trash2, X, User } from 'lucide-react';
import { eliminarDescanso } from '@/components/hooks/useDescansos';
import { toast } from 'sonner';

export interface DescansoSeleccionado {
  id_descanso: string;
  fecha: string;
  hora_inicio: string;
  hora_fin: string;
  motivo: string | null;
  /** Solo cuando quien pincha puede ver descansos de varios trabajadores a
   *  la vez (calendario de empresa sin filtrar). En el calendario propio del
   *  trabajador no hace falta: siempre es el suyo. */
  nombreTrabajador?: string | null;
  /** Si el usuario actual no es el dueño ni admin, el borrado fallará por
   *  RLS; se deja intentar igualmente y se muestra el error si lo hay. */
}

interface Props {
  x: number;
  y: number;
  descanso: DescansoSeleccionado;
  onClose: () => void;
  onEliminado: () => void;
}

const ANCHO_MENU = 240;

const formatHora = (h: string) => h.slice(0, 5);
const formatFecha = (f: string) =>
  new Date(`${f}T00:00:00`).toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

const BreakDetailPopover = ({ x, y, descanso, onClose, onEliminado }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [borrando, setBorrando] = useState(false);

  useEffect(() => {
    const fuera = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', escape);
    };
  }, [onClose]);

  const left = Math.min(x, window.innerWidth - ANCHO_MENU - 12);
  const top = Math.min(y, window.innerHeight - 160);

  const handleEliminar = async () => {
    if (borrando) return;
    setBorrando(true);
    const ok = await eliminarDescanso(descanso.id_descanso);
    setBorrando(false);
    if (!ok) return;
    toast.success('Descanso eliminado');
    onEliminado();
  };

  return createPortal(
    <div
      ref={ref}
      className="fixed z-[9999] w-[240px] rounded-xl bg-white dark:bg-[#1c1c1e] shadow-xl border border-gray-100 dark:border-white/10 overflow-hidden"
      style={{ top, left }}
    >
      <div className="flex items-start justify-between gap-2 px-3 pt-3">
        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 font-medium text-sm">
          <Coffee className="w-4 h-4 shrink-0" />
          {descanso.motivo?.trim() || 'Descanso'}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-3 pt-1 pb-3 text-xs text-gray-500 dark:text-gray-400">
        <p className="capitalize">
          {formatFecha(descanso.fecha)} · {formatHora(descanso.hora_inicio)}–
          {formatHora(descanso.hora_fin)}
        </p>
        {descanso.nombreTrabajador && (
          <p className="mt-0.5 flex items-center gap-1">
            <User className="w-3 h-3 shrink-0" />
            {descanso.nombreTrabajador}
          </p>
        )}
      </div>

      <div className="h-px bg-gray-100 dark:bg-white/10" />

      <button
        type="button"
        onClick={handleEliminar}
        disabled={borrando}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 disabled:opacity-60"
      >
        <Trash2 className="w-4 h-4" />
        {borrando ? 'Eliminando…' : 'Eliminar descanso'}
      </button>
    </div>,
    document.body
  );
};

export default BreakDetailPopover;